import { inferRouteObject } from 'react-router-typed-object';

import { CampaignStatus } from '@/services/ws-digital/src/main/resources/rest-api/types';
import { CampaignPositionType } from '@/services/ws-orders/src/main/resources/rest-api';
import { generatePath, Navigate } from '@/js/libs/router';
import { PrivateRoute, Roles, and, not, or } from '@/js/libs/use-auth';

import { PositionsType } from 'src/components/PositionsStageFilter';
import type { Condition } from 'src/components/RoleBasedRedirector';
import RoleBasedRedirector from 'src/components/RoleBasedRedirector';
import RouteParamsValidator from 'src/components/RouteParamsValidator';
import Campaign from 'src/scenes/Campaign';
import { createCampaignTypeValidator } from 'src/scenes/Campaign/helpers';
import Orders from 'src/scenes/Orders';
import { BroadcastRoleChecker } from 'src/scenes/Orders/containers/BroadcastPositionsV2/components/BroadcastRoleChecker';
import { validateBroadcastType } from 'src/scenes/Orders/containers/BroadcastPositionsV2/helpers';
import { campaignsListRouterSearchParamsSchema } from 'src/scenes/Orders/containers/Campaigns/pages/CampaignsList/constants';
import { validateBroadcastPositionStages } from 'src/scenes/Orders/helpers';
import { BroadcastPositionStage } from 'src/scenes/Orders/types';
import SelfBooking from 'src/scenes/SelfBookingTool';
import Status from 'src/scenes/Status';

const index = true;

const ordersConditions: Condition[] = [
  { roles: Roles.POSTNETZ_BRANCH_USER, to: generatePath('/orders/booking') },
  { roles: Roles.POSTADMANAGER, to: generatePath('/orders/post-campaigns') },
  { roles: Roles.AGENCY, to: generatePath('/orders/campaigns') },
];

export const orders1RouteObject = inferRouteObject({
  path: 'orders',
  Component: Orders.Helmet,
  children: [
    {
      index,
      element: <RoleBasedRedirector conditions={ordersConditions} noAccessElement={<Status.NotFound />} />,
    },
    {
      path: PositionsType.DIGITAL,
      element: (
        <PrivateRoute
          roles={and(
            or(Roles.MANAGE_DIGITAL_PRODUCTS, Roles.BOOK_CAMPAIGNS, Roles.MANAGE_ACCOUNTS, Roles.SELF_BOOKING, Roles.VIEW_CAMPAIGNS),
            not(Roles.POSTNETZ_BRANCH_USER),
          )}
          noAccessComponent={Status.NotFound}
        />
      ),
      children: [
        { index, element: <Navigate to={`./${CampaignStatus.RUNNING}`} replace /> },
        { path: ':status', Component: Orders.DigitalPositions },
      ],
    },
    {
      path: 'digital-positions-v2',
      element: (
        <PrivateRoute
          roles={and(
            or(Roles.MANAGE_DIGITAL_PRODUCTS, Roles.BOOK_CAMPAIGNS, Roles.MANAGE_ACCOUNTS, Roles.SELF_BOOKING, Roles.VIEW_CAMPAIGNS),
            not(Roles.POSTNETZ_BRANCH_USER),
          )}
          noAccessComponent={Status.NotFound}
        />
      ),
      children: [{ index, Component: Orders.DigitalPositionsV2 }],
    },
    {
      path: PositionsType.PRINT,
      element: (
        <PrivateRoute roles={and(Roles.MANAGE_PRINT_PRODUCTS, not(Roles.POSTNETZ_BRANCH_USER))} noAccessComponent={Status.NotFound} />
      ),
      children: [
        { index, element: <Navigate to={`./${CampaignStatus.BOOKED}`} replace /> },
        { path: ':status', Component: Orders.PrintPositions },
      ],
    },
    {
      path: 'print-positions-v2',
      element: (
        <PrivateRoute roles={and(Roles.MANAGE_PRINT_PRODUCTS, not(Roles.POSTNETZ_BRANCH_USER))} noAccessComponent={Status.NotFound} />
      ),
      children: [{ index, Component: Orders.PrintPositionsV2 }],
    },
    {
      path: PositionsType.RADIO,
      element: (
        <PrivateRoute roles={and(Roles.MANAGE_RADIO_PRODUCTS, not(Roles.POSTNETZ_BRANCH_USER))} noAccessComponent={Status.NotFound} />
      ),
      children: [
        { index, element: <Navigate to={`./${BroadcastPositionStage.ALL}`} replace /> },
        {
          path: ':stage',
          element: <RouteParamsValidator validate={validateBroadcastPositionStages} invalidStateElement={<Status.NotFound />} />,
          children: [{ index, element: <Orders.Broadcast broadcastType={CampaignPositionType.RADIO} /> }],
        },
      ],
    },
    {
      path: PositionsType.TV,
      element: <PrivateRoute roles={and(Roles.MANAGE_TV_PRODUCTS, not(Roles.POSTNETZ_BRANCH_USER))} noAccessComponent={Status.NotFound} />,
      children: [
        { index, element: <Navigate to={`./${BroadcastPositionStage.ALL}`} replace /> },
        {
          path: ':stage',
          element: <RouteParamsValidator validate={validateBroadcastPositionStages} invalidStateElement={<Status.NotFound />} />,
          children: [{ index, element: <Orders.Broadcast broadcastType={CampaignPositionType.TV} /> }],
        },
      ],
    },
    {
      path: ':broadcastType',
      element: <RouteParamsValidator validate={validateBroadcastType} invalidStateElement={<Status.NotFound />} />,
      children: [
        {
          Component: BroadcastRoleChecker,
          children: [{ index, Component: Orders.BroadcastPositionsV2 }],
        },
      ],
    },
    {
      path: PositionsType.POS,
      element: <PrivateRoute roles={and(Roles.MANAGE_POS_PRODUCTS, not(Roles.POSTNETZ_BRANCH_USER))} noAccessComponent={Status.NotFound} />,
      children: [{ index, element: <Orders.PointOfSalePositions /> }],
    },
    {
      path: 'campaigns',
      element: (
        <PrivateRoute
          roles={and(
            or(Roles.ADMIN, Roles.BOOK_CAMPAIGNS, Roles.SELF_BOOKING, Roles.AGENCY),
            not(Roles.POSTADMANAGER),
            not(Roles.POSTNETZ_BRANCH_USER),
          )}
          noAccessComponent={Status.NotFound}
        />
      ),
      children: [
        {
          Component: Campaign.Helmet,
          children: [
            {
              element: <PrivateRoute roles={Roles.AGENCY} noAccessComponent={Status.NotFound} />,
              children: [{ index, Component: Orders.Campaigns.CampaignsList }],
            },
            {
              path: ':type',
              element: <PrivateRoute roles={not(and(Roles.YALEO_SELF_BOOKING, not(Roles.ADMIN)))} noAccessComponent={Status.NotFound} />,
              children: [
                {
                  index,
                  element: <Navigate to='/orders/campaigns' replace />,
                },
                {
                  element: (
                    <RouteParamsValidator
                      validate={createCampaignTypeValidator(Object.keys(CampaignStatus))}
                      invalidStateElement={<Status.NotFound />}
                    />
                  ),
                },
                {
                  element: (
                    <RouteParamsValidator
                      validate={createCampaignTypeValidator(CampaignStatus.OFFERED)}
                      invalidStateElement={<Status.NotFound />}
                    />
                  ),
                  children: [
                    {
                      element: <PrivateRoute roles={or(Roles.ADMIN, Roles.BOOK_CAMPAIGNS)} noAccessComponent={Status.NotFound} />,
                      children: [
                        {
                          path: 'new-order',
                          children: [
                            { index, Component: Campaign.Edit },
                            { path: ':accountId', Component: Campaign.Edit },
                          ],
                        },
                        {
                          path: 'new-order-alfa',
                          children: [
                            { index, Component: Orders.Campaigns.Order },
                            { path: ':accountId', Component: Orders.Campaigns.Order },
                          ],
                        },
                      ],
                    },
                    {
                      element: <PrivateRoute roles={or(Roles.ADMIN, Roles.BOOK_CAMPAIGNS)} noAccessComponent={Status.NotFound} />,
                      children: [
                        {
                          path: 'new-order-alfa',
                          children: [
                            { index, Component: Orders.Campaigns.Order },
                            { path: ':accountId', Component: Orders.Campaigns.Order },
                          ],
                        },
                      ],
                    },
                    {
                      path: 'tasks/:taskId/new-order',
                      Component: Campaign.Edit,
                    },
                    {
                      path: 'tasks/:taskId/new-order-alfa',
                      Component: Orders.Campaigns.Order,
                    },
                  ],
                },
                {
                  path: ':campaignId',
                  children: [
                    {
                      element: <PrivateRoute roles={or(Roles.ADMIN, Roles.BOOK_CAMPAIGNS)} noAccessComponent={Status.NotFound} />,
                      children: [
                        {
                          Component: Campaign.EditPermissionChecker,
                          children: [
                            { path: 'edit', Component: Campaign.Edit },
                            { path: 'edit-alfa', Component: Orders.Campaigns.Order },
                            { path: 'edit-positions/:positionType?', Component: Campaign.EditPositions },
                            {
                              path: 'edit-positions/:positionType/:hasPositionConflict?',
                              Component: Campaign.EditPositions,
                            },
                            { path: 'preview-pdf', Component: Campaign.DocumentPreview },
                          ],
                        },
                        {
                          path: 'history',
                          Component: Campaign.History,
                        },
                      ],
                    },
                    {
                      element: <PrivateRoute roles={Roles.AGENCY} noAccessComponent={Status.NotFound} />,
                      children: [
                        {
                          Component: Campaign.PaymentDetails.Providers,
                          children: [
                            {
                              path: 'payment',
                              Component: Campaign.PaymentDetails.Component,
                            },
                          ],
                        },
                        {
                          Component: Campaign.Overview.Providers,
                          children: [
                            {
                              index,
                              Component: Campaign.Overview.Component,
                            },
                          ],
                        },
                      ],
                    },
                    {
                      path: 'sb-history',
                      Component: SelfBooking.HistoryPage,
                    },
                  ],
                },
                {
                  element: <PrivateRoute roles={or(Roles.ADMIN, Roles.SELF_BOOKING)} noAccessComponent={Status.NotFound} />,
                  children: [
                    {
                      path: 'sb',
                      children: [
                        { path: 'new-order/campaign', Component: SelfBooking.CampaignPage },
                        {
                          path: ':campaignId',
                          children: [
                            { index, Component: SelfBooking.CampaignPage },
                            {
                              path: 'position',
                              children: [
                                { path: 'create', Component: SelfBooking.PositionPage },
                                { path: ':positionId', Component: SelfBooking.PositionPage },
                              ],
                            },
                            { path: 'preview-pdf', Component: SelfBooking.PreviewPage },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
      searchParams: campaignsListRouterSearchParamsSchema.parse,
    },
  ],
});
